import React, { useState, useEffect } from "react";
import api from "../api";
import PageLayout from "../components/PageLayout";
import { formatDateTime } from "../utils/formatDate";

import { useParams } from 'react-router-dom';

const SSLChecker = () => {
  const { domain: paramDomain } = useParams();
  const [domain, setDomain] = useState(paramDomain || "");
  const [sslResult, setSSLResult] = useState(null);
  const [whoisResult, setWhoisResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [scanResult, setScanResult] = useState(null);

  // Auto-load domain from URL if provided
  useEffect(() => {
    if (paramDomain) {
      setDomain(paramDomain);
      // Auto trigger scan after short delay (to ensure state updates)
      setTimeout(() => {
        handleScan(paramDomain);
      }, 500);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paramDomain]);

  // Save SSL scan details to server when scanResult updates
  useEffect(() => {
    if (!scanResult) return; // Only run when results exist

    const saveDetails = async () => {
      try {
        await api.post(`/api/domain-details/save`, {
          domain,
          certificate: scanResult.certificate,
          issuerCertificate: scanResult.issuerCertificate,
          tlsInfo: scanResult.tlsInfo,
          ocspInfo: scanResult.ocsp,
          crlInfo: scanResult.crl,
          whoisInfo: scanResult.whois
        });

        // Success handled via state

      } catch (err) {
        console.error("Error saving domain details:", err);
      }
    };

    saveDetails();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scanResult]);  // runs only when scanResult changes

  const handleScan = async (domainToScan = domain, isRefresh = false) => {
    if (!domainToScan) return;

    setLoading(true);
    setError(null);
    setSSLResult(null);
    setWhoisResult(null);

    try {
      // ------------------------
      // 1. SSL Scan (pass refresh flag if needed)
      // ------------------------
      const sslRes = await api.get(
        `/api/ssl-check?url=${domainToScan}${isRefresh ? '&refresh=true' : ''}`
      );
      setSSLResult(sslRes.data);

      // ------------------------
      // 2. WHOIS Lookup
      // ------------------------
      const cleanDomain = domainToScan
        .replace(/^https?:\/\//, "")
        .replace(/\/$/, "");

      const whoisRes = await api.get(
        `/api/whois?domain=${cleanDomain}`
      );
      setWhoisResult(whoisRes.data);

      // ------------------------
      // 3. Get Current User
      // ------------------------
      const { data: user } = await api.get(
        `/api/auth/current-user`,
        { withCredentials: true }
      );

      // ------------------------
      // 4. Save domain for user
      // ------------------------
      if (user && user.id) {
        await api.post(
          `/api/update-status`,
          {
            domain: cleanDomain,
            user_id: user.id,
            ssl_data: sslRes.data,
            whois_data: whoisRes.data
          },
          { withCredentials: true }
        );
      }

      // ------------------------
      // 5. (important) update scanResult → triggers saveDetails()
      // ------------------------
      setScanResult({
        certificate: sslRes.data?.certificate,
        issuerCertificate: sslRes.data?.issuerCertificate,
        tlsInfo: sslRes.data?.tlsInfo,
        ocsp: sslRes.data?.ocsp,
        crl: sslRes.data?.crl,
        whois: whoisRes.data,
      });

    } catch (err) {
      console.error("SCAN ERROR:", err);
      setError(err.response?.data?.error || err.message);
    } finally {
      setLoading(false);
    }
  };

  const Badge = ({ children, color }) => (
    <span className={`badge bg-${color} me-1`}>{children}</span>
  );

  // ---------- SSL Section ----------
  const renderCertificate = (cert, title = "Certificate") => {
    if (!cert) return null;
    return (
      <div className="card mb-3 shadow-sm">
        <div className="card-header bg-primary text-white">{title}</div>
        <div className="card-body">
          <table className="table table-sm table-bordered mb-2">
            <tbody>
              <tr>
                <th>Subject</th>
                <td>
                  {cert.subject?.CN || JSON.stringify(cert.subject)}{" "}
                  {(() => {
                    const cn = cert.subject?.CN?.toLowerCase();
                    const cleanDomain = domain
                      .replace(/^https?:\/\//, "")
                      .replace(/\/$/, "")
                      .toLowerCase();

                    if (cn && !cleanDomain.includes(cn) && !cn.includes(cleanDomain)) {
                      return (
                        <span className="badge bg-danger ms-2">
                          CN Mismatch: does not match domain
                        </span>
                      );
                    }
                    return null;
                  })()}
                </td>
              </tr>
              <tr>
                <th>Issuer</th>
                <td>{cert.issuer?.CN || JSON.stringify(cert.issuer)}</td>
              </tr>
              <tr>
                <th>Validity</th>
                <td>
                  From: {formatDateTime(cert.validFrom)} <br />
                  To: {formatDateTime(cert.validTo)}{" "}
                  {new Date(cert.validTo) < new Date() ? (
                    <Badge color="danger">Expired</Badge>
                  ) : (
                    <Badge color="success">Valid</Badge>
                  )}
                </td>
              </tr>
              <tr>
                <th>Serial Number</th>
                <td>{cert.serialNumber}</td>
              </tr>
              <tr>
                <th>Fingerprint (SHA256)</th>
                <td>{cert.fingerprint256 || cert.fingerprint}</td>
              </tr>
              <tr>
                <th>Key</th>
                <td>{cert.key || "-"}</td>
              </tr>
              <tr>
                <th>Alt Names</th>
                <td>{cert.altNames || "-"}</td>
              </tr>
              <tr>
                <th>Signature Algorithm</th>
                <td>{cert.signatureAlgorithm || "-"}</td>
              </tr>
            </tbody>
          </table>

          {cert.issuerCertificate &&
            renderCertificate(cert.issuerCertificate, "Issuer Certificate")}
        </div>
      </div>
    );
  };

  const renderTLSInfo = (tls) => {
    if (!tls) return null;
    return (
      <div className="card mb-3 shadow-sm">
        <div className="card-header bg-success text-white">TLS Info</div>
        <div className="card-body">
          <p>
            <strong>Negotiated Version:</strong> {tls.negotiatedVersion || "-"}{" "}
            <Badge color={tls.authorized ? "success" : "danger"}>
              {tls.authorized ? "Authorized" : "Unauthorized"}
            </Badge>{" "}
            {tls.authorizationError && (
              <Badge color="warning">{tls.authorizationError}</Badge>
            )}
          </p>

          {tls.supportedVersions && Array.isArray(tls.supportedVersions) && tls.supportedVersions.length > 0 && (
            <>
              <h6>Supported Versions</h6>
              <table className="table table-sm table-bordered mb-2">
                <thead>
                  <tr>
                    <th>Version</th>
                    <th>Supported</th>
                  </tr>
                </thead>
                <tbody>
                  {tls.supportedVersions.map((v) => (
                    <tr key={v.version}>
                      <td>{v.version}</td>
                      <td>{v.supported ? "✅" : "❌"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          )}

          {tls.cipherSuites && tls.cipherSuites.length > 0 && (
            <>
              <h6>Cipher Suites</h6>
              <div>
                {tls.cipherSuites.map((c, idx) => (
                  <Badge key={idx} color="secondary">
                    {c}
                  </Badge>
                ))}
              </div>
            </>
          )}

          {tls.forwardSecrecy !== undefined && (
            <div className="mt-2">
              <strong>Forward Secrecy:</strong>{" "}
              {tls.forwardSecrecy ? (
                <Badge color="success">Yes</Badge>
              ) : (
                <Badge color="danger">No</Badge>
              )}
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderRevocation = (rev) => {
    if (!rev) return null;

    return (
      <div className="card mb-3 shadow-sm">
        <div className="card-header bg-warning text-dark">Revocation / OCSP</div>
        <div className="card-body">
          <h6>OCSP</h6>
          <p>
            <strong>Status:</strong> {rev.ocsp?.result?.status || "Unknown"} <br />
            <strong>Detail:</strong> {rev.ocsp?.result?.detail || "-"} <br />
            <strong>URLs:</strong> {rev.ocsp?.urls?.length ? rev.ocsp.urls.join(", ") : "-"}
          </p>

          <h6>CRL</h6>
          <p>
            <strong>URLs:</strong> {rev.crl?.urls?.length ? rev.crl.urls.join(", ") : "-"} <br />
            <strong>Checks:</strong> {rev.crl?.checks?.length ? rev.crl.checks.join(", ") : "-"}
          </p>
        </div>
      </div>
    );
  };


  const renderCAA = (records) => {
    if (!records || records.length === 0) return null;
    return (
      <div className="card mb-3 shadow-sm">
        <div className="card-header bg-info text-white">DNS CAA Records</div>
        <div className="card-body">
          {records.map((r, idx) => (
            <Badge key={idx} color="dark">
              {r}
            </Badge>
          ))}
        </div>
      </div>
    );
  };

  // ---------- WHOIS Section ----------
  const renderWhois = (whois) => {
    if (!whois) return null;
    return (
      <div className="card mb-3 shadow-sm">
        <div className="card-header bg-secondary text-white">WHOIS Info</div>
        <div className="card-body">
          <table className="table table-sm table-bordered mb-0">
            <tbody>
              <tr>
                <th>Domain</th>
                <td>{whois.domain}</td>
              </tr>
              <tr>
                <th>Registrar</th>
                <td>{whois.registrar}</td>
              </tr>
              <tr>
                <th>Creation Date</th>
                <td>{whois.creation_date}</td>
              </tr>
              <tr>
                <th>Expiry Date</th>
                <td>{whois.expiry_date}</td>
              </tr>
              <tr>
                <th>Updated Date</th>
                <td>{whois.updated_date}</td>
              </tr>
              <tr>
                <th>Nameservers</th>
                <td>{whois.nameservers?.join(", ") || "-"}</td>
              </tr>
              <tr>
                <th>Status</th>
                <td>{whois.status?.join(", ") || "-"}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  return (
    <PageLayout title="SSL & WHOIS Scan">
      <div className="row mb-4">
        <div className="col-12">
          <div className="card shadow-sm">
            <div className="card-body">
              <div className="d-flex justify-content-between align-items-center mb-0">
                <h4 className="mb-0">
                  SSL & WHOIS Scan for:{" "}
                  <span className="text-primary">{domain || "Loading..."}</span>
                </h4>
                <div className="d-flex gap-2">
                  <button 
                    className="btn btn-primary btn-sm" 
                    onClick={() => handleScan(domain, true)}
                    disabled={loading}
                  >
                    <i className={`las la-sync-alt ${loading ? 'la-spin' : ''} me-1`}></i>
                    Refresh Scan
                  </button>
                  <a href="/scans" className="btn btn-outline-dark btn-sm">
                    ← Back to My Scans
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {loading && (
        <div className="text-center my-5">
          <div className="spinner-border text-danger" role="status">
            <span className="visually-hidden">Scanning...</span>
          </div>
          <p className="mt-3 text-muted">Scanning {domain}...</p>
        </div>
      )}

      {error && <p className="mt-3 text-danger">{error}</p>}

      {!loading && sslResult && (
        <>
          {renderCertificate(sslResult.certificate)}
          {renderTLSInfo(sslResult.tls)}
          {renderRevocation(sslResult.revocation)}
          {renderCAA(sslResult.dnsCAA)}
        </>
      )}

      {!loading && whoisResult && renderWhois(whoisResult)}

      {!loading && !domain && (
        <div className="alert alert-warning mt-4">
          No domain specified. Please access this page as:
          <br />
          <code>?domain=example.com</code>
        </div>
      )}
    </PageLayout>
  );
};

export default SSLChecker;
